import { apiClient } from '@/core/api';
import type { CurrentUser } from '@/stores/session';
import type { JwtResponse, LoginInput, RegisterInput } from './types';

/** `POST /api/users/auth/login/` — dj-rest-auth LoginView (email + password). */
export async function login(input: LoginInput): Promise<JwtResponse> {
  const { data } = await apiClient.post<JwtResponse>('/users/auth/login/', input);
  return data;
}

/** `POST /api/users/auth/register/` — dj-rest-auth RegisterView.
 *  Returns tokens (USE_JWT) or `{ detail }` when email verification is mandatory. */
export async function register(input: RegisterInput): Promise<JwtResponse> {
  const { data } = await apiClient.post<JwtResponse>('/users/auth/register/', input);
  return data;
}

export async function logout(): Promise<void> {
  // Best-effort server logout; the client clears tokens regardless.
  try {
    await apiClient.post('/users/auth/logout/');
  } catch {
    /* ignore — local sign-out still proceeds */
  }
}

/** `GET /api/users/current-user/` — profile + memberships singleton. */
export async function getCurrentUser(): Promise<CurrentUser> {
  const { data } = await apiClient.get<CurrentUser>('/users/current-user/');
  return data;
}

// ---- Password reset -----------------------------------------------------------

/** Request a reset email ("Forgot password?"). */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiClient.post('/users/auth/password/reset/', { email });
}

export interface PasswordResetConfirmInput {
  uid: string;
  token: string;
  new_password1: string;
  new_password2: string;
}

/** Set the password from the emailed reset link (uid + token). */
export async function confirmPasswordReset(input: PasswordResetConfirmInput): Promise<void> {
  await apiClient.post('/users/auth/password/reset/confirm/', input);
}

/** Extract the access/refresh pair; empty strings when the response has none
 *  (e.g. register with mandatory email verification). */
export function tokensFrom(res: JwtResponse): { access: string; refresh: string } {
  return { access: res.access ?? '', refresh: res.refresh ?? '' };
}
